import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { sendOtp, verifyOtp } from "core/Services/AuthServices";

interface CodeVerifyFormData {
  email: string;
  otp: string;
}

const CodeVerify = () => {
  const { email: emailParam } = useParams<{ email?: string }>();
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CodeVerifyFormData>();

  useEffect(() => {
    if (emailParam) {
      setValue("email", emailParam.replace(":", ""));
      setIsOtpSent(true);
    }
  }, [emailParam, setValue]);

  const handleSendOtp = async (data: { email: string }) => {
    const { email } = data;

    setIsLoading(true);
    try {
      await sendOtp(email);
      toast.success(t("auth.otpSent"));
      setIsOtpSent(true);
    } catch (error: any) {
      toast.error(error.message || t("auth.otpError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (data: CodeVerifyFormData) => {
    const { email, otp } = data;

    setIsLoading(true);
    try {
      await verifyOtp(email, otp);
      toast.success(t("auth.verifySuccess"));
      navigate("/");
    } catch (error: any) {
      toast.error(error.response.data.message || t("auth.verifyError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 text-center">
          {t("auth.verifyEmail")}
        </h2>
        <form
          onSubmit={handleSubmit(isOtpSent ? handleVerifyOtp : handleSendOtp)}
          className="space-y-4"
        >
          <FormInput
            label={t("common.email")}
            name="email"
            type="email"
            register={register}
            errors={errors}
            placeholder={t("common.email")}
            disabled={!!emailParam}
          />
          {isOtpSent && (
            <FormInput
              label={t("auth.enterOTP")}
              name="otp"
              type="text"
              register={register}
              errors={errors}
              placeholder={t("auth.enterOTP")}
            />
          )}
          <Button
            type="submit"
            fullWidth
            isLoading={isLoading}
            disabled={isLoading}
          >
            {isLoading
              ? isOtpSent
                ? t("auth.verifying")
                : t("auth.sendingOTP")
              : isOtpSent
              ? t("auth.verifyOTP")
              : t("auth.sendOTP")}
          </Button>
          {isOtpSent && (
            <button
              type="button"
              className="w-full text-center text-blue-600 hover:text-blue-800 underline text-sm mt-2"
              disabled={isLoading}
              onClick={() =>
                handleSendOtp({ email: emailParam?.replace(":", "") || "" })
              }
            >
              {isLoading ? t("auth.resending") : t("auth.resendOTP")}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default CodeVerify;
